import { useState, type ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useCompanyInfoByInvoice, type CompanyInfoByInvoice } from "components/invoiceApi/companyInfoApi";

interface InvoiceSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (company: CompanyInfoByInvoice) => void;
  initialSearchTerm?: string;
  subTitle?: ReactNode;
}

export default function InvoiceSearchModal({
  open,
  onClose,
  onSelect,
  initialSearchTerm = "",
  subTitle,
}: InvoiceSearchModalProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [hasSearched, setHasSearched] = useState(false);
  const { getCompanyInfo, isLoading, error, companyInfoList } = useCompanyInfoByInvoice();

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    // Basic invoice number format validation: T + 13 digits
    const invoicePattern = /^T\d{13}$/;
    if (!invoicePattern.test(searchTerm.trim())) {
      // Mark as searched to show appropriate message state
      setHasSearched(true);
      return;
    }

    try {
      await getCompanyInfo(searchTerm.trim());
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching for invoice/company:", error);
      setHasSearched(true);
    }
  };

  const handleSelect = (company: CompanyInfoByInvoice) => {
    onSelect(company);
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>インボイス番号検索</DialogTitle>
      <DialogContent>
        {subTitle && (
          <Box sx={{ mb: 4 }}>
            {subTitle}
          </Box>
        )}
        <Box sx={{ display: "flex", mb: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="インボイス番号"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
            placeholder="例: T1234567890123"
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading || !searchTerm.trim()}
          >
            検索
          </Button>
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error from API hook */}
        {error && (
          <Typography color="error" sx={{ my: 2 }}>
            エラーが発生しました: {error}
          </Typography>
        )}

        {/* If invalid format was searched */}
        {hasSearched && !isLoading && !error && companyInfoList.length === 0 && (
          <Typography sx={{ my: 2 }}>
            検索結果がありません。インボイス番号を確認して再度検索してください。
          </Typography>
        )}

        {!isLoading && companyInfoList.length > 0 && (
          <List sx={{ maxHeight: "400px", overflow: "auto" }}>
            {companyInfoList.map((company, index) => (
              <ListItem key={index} disablePadding divider>
                <ListItemButton onClick={() => handleSelect(company)}>
                  <ListItemText
                    primary={company.clientCompanyName}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" display="block">
                          インボイス番号: {company.invoiceNumber}
                        </Typography>
                        <Typography component="span" variant="body2" display="block">
                          {company.prefecture}
                          {company.address1}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
      </DialogActions>
    </Dialog>
  );
}
